// Cash's line (cash where-we-stand Part 2, DD-054): where we stand each day of the range, one value a day. It is solid up
// to today and dashed ahead, where it counts each contract's next periods as a close would book them (TD-076), with today
// marked by a dot on a hairline. Without the agency's cash there is no level (A4), so it draws each day's change from today
// instead, on a quiet zero line that is today's level. Hovering or focusing the plot snaps to the nearest day and shows its
// figure; the arrow keys walk the days. One series, so no legend: the section's title names it. Marks follow the dataviz
// rules: a 2px line, hairline solid grid, an 8px dot ringed in the surface colour, never red (a line is not a balance
// cell). The axis maths here only places marks. Every figure is the server's (TD-102).
import { useLayoutEffect, useRef, useState, type KeyboardEvent, type PointerEvent } from "react";
import { formatMoney } from "@/utils/currency";
import type { LineDay } from "../types";
import { dateLabel, dayMonthLabel } from "../utils/format";
import { fromToday, signedMoney } from "./cashText";

const H = 240;
const PAD = { top: 20, right: 16, bottom: 28, left: 84 };

/** A round tick step for a span of minor units: 1, 2 or 5 times a power of ten, about a quarter of the span. */
export function niceStep(span: number): number {
  const raw = span / 4;
  const mag = 10 ** Math.floor(Math.log10(Math.max(raw, 1)));
  const n = raw / mag;
  return (n <= 1 ? 1 : n <= 2 ? 2 : n <= 5 ? 5 : 10) * mag;
}

interface Props {
  line: LineDay[];
  today: string;
}

export function CashChart({ line, today }: Props) {
  const box = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(800);
  const [hover, setHover] = useState<number | null>(null);
  useLayoutEffect(() => {
    const el = box.current;
    // jsdom has no ResizeObserver, so the chart keeps its default width there.
    if (!el || typeof ResizeObserver === "undefined") return;
    const fit = () => setWidth(el.clientWidth);
    fit();
    const observer = new ResizeObserver(fit);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  // The box renders on an empty line too, so the observer is attached when the line fills.
  if (line.length === 0) return <div ref={box} className="relative" />;

  // The level when every day has one, else the change from today (A4).
  const levels = line.every((d) => d.positionMinor !== null);
  const value = (d: LineDay): number => (levels && d.positionMinor !== null ? d.positionMinor : d.changeMinor);
  const money = (minor: number): string => (levels ? formatMoney(minor, "EUR") : signedMoney(minor));
  const values = line.map(value);
  // A change always shows its zero, today's level; a level fits its own span.
  const min = Math.min(...values, ...(levels ? [] : [0]));
  const max = Math.max(...values, ...(levels ? [] : [0]));
  const step = niceStep(max - min || 100_00);
  let lo = Math.floor(min / step) * step;
  let hi = Math.ceil(max / step) * step;
  // A flat line sits mid-plot, one step either side.
  if (hi === lo) {
    lo -= step;
    hi += step;
  }
  const plotW = width - PAD.left - PAD.right;
  const plotH = H - PAD.top - PAD.bottom;
  const x = (i: number) => PAD.left + (line.length < 2 ? 0 : (i / (line.length - 1)) * plotW);
  const y = (v: number) => PAD.top + (1 - (v - lo) / (hi - lo)) * plotH;
  const ticks: number[] = [];
  for (let v = lo; v <= hi; v += step) ticks.push(v);

  const last = line.length - 1;
  const todayAt = Math.max(0, line.findIndex((d) => d.date === today));
  const path = (from: number, to: number) =>
    line
      .slice(from, to + 1)
      .map((d, k) => `${k === 0 ? "M" : "L"}${x(from + k).toFixed(1)},${y(value(d)).toFixed(1)}`)
      .join(" ");
  const labelDays = Array.from(new Set([0, todayAt, last]));
  const clamp = (i: number) => Math.min(last, Math.max(0, i));

  const onMove = (e: PointerEvent<SVGRectElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setHover(clamp(Math.round(((e.clientX - rect.left) / (rect.width || 1)) * last)));
  };
  const onKey = (e: KeyboardEvent<SVGRectElement>) => {
    const at = hover ?? todayAt;
    const keys: Record<string, number> = { ArrowLeft: at - 1, ArrowRight: at + 1, Home: 0, End: last };
    const next: number | undefined = keys[e.key];
    if (next === undefined) return;
    e.preventDefault();
    setHover(clamp(next));
  };
  const hovered = hover === null ? null : line[hover];

  return (
    <div ref={box} className="relative">
      <svg width={width} height={H} role="img" aria-label={`Where we stand, ${dateLabel(line[0].date)} to ${dateLabel(line[last].date)}`}>
        {ticks.map((v) => (
          <g key={v}>
            <line x1={PAD.left} x2={width - PAD.right} y1={y(v)} y2={y(v)} className={!levels && v === 0 ? "stroke-gray-400" : "stroke-gray-100"} strokeWidth={1} />
            <text x={PAD.left - 8} y={y(v)} dy="0.32em" textAnchor="end" className="fill-gray-400 text-[11px] tabular-nums">
              {money(v)}
            </text>
          </g>
        ))}
        {labelDays.map((i) => (
          <text key={i} x={x(i)} y={H - 8} textAnchor={i === 0 ? "start" : i === last ? "end" : "middle"} className="fill-gray-500 text-[11px]">
            {i === todayAt ? "Today" : dayMonthLabel(line[i].date)}
          </text>
        ))}
        <line x1={x(todayAt)} x2={x(todayAt)} y1={PAD.top} y2={H - PAD.bottom} className="stroke-gray-200" strokeWidth={1} />
        <g className="text-indigo-600" fill="none" stroke="currentColor" strokeWidth={2} strokeLinejoin="round" strokeLinecap="round">
          <path d={path(0, todayAt)} />
          <path d={path(todayAt, last)} strokeDasharray="5 4" />
        </g>
        <circle cx={x(todayAt)} cy={y(value(line[todayAt]))} r={4} className="fill-indigo-600 stroke-white" strokeWidth={2} />
        {hovered && hover !== null && (
          <g pointerEvents="none">
            <line x1={x(hover)} x2={x(hover)} y1={PAD.top} y2={H - PAD.bottom} className="stroke-gray-300" strokeWidth={1} />
            <circle cx={x(hover)} cy={y(value(hovered))} r={4} className="fill-indigo-600 stroke-white" strokeWidth={2} />
          </g>
        )}
        <rect
          x={PAD.left}
          y={PAD.top}
          width={plotW}
          height={plotH}
          fill="transparent"
          tabIndex={0}
          className="outline-none"
          onPointerMove={onMove}
          onPointerLeave={() => setHover(null)}
          onFocus={() => setHover((at) => at ?? todayAt)}
          onBlur={() => setHover(null)}
          onKeyDown={onKey}
        />
      </svg>
      {hovered && hover !== null && (
        <div
          role="status"
          className="pointer-events-none absolute top-1 rounded-lg bg-white px-3 py-2 text-xs shadow-lg ring-1 ring-gray-200"
          style={{ left: Math.min(Math.max(x(hover) - 80, 0), width - 170) }}
        >
          <div className="font-semibold tabular-nums text-gray-900">{levels ? money(value(hovered)) : fromToday(hovered.changeMinor)}</div>
          <div className="text-gray-500">
            {dateLabel(hovered.date)}
            {hovered.date === today ? " · today" : ""}
          </div>
        </div>
      )}
    </div>
  );
}
